import React, { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from 'react-i18next'
import { Dropdown, Modal } from 'react-bootstrap'
import DatePicker from "react-datepicker"
import Select from 'react-select'
import { useForm } from "react-hook-form"

import FilterSearch from 'pages/parts/FilterSearch'
import TableControl from 'pages/parts/TableControl'

import { PGET, GET, PATCH } from 'helpers/api'
import XLSX from 'xlsx-js-style'
import { formatMoney, returnSign, formatDateBackend, findFromArrayById, formatDate, regexPhoneNumber, regexNumeric } from 'helpers/helpers'
import Pagination, { ItemPerPage } from "pages/parts/Pagination"

function Index() {
	const { t } = useTranslation()
	const reduxAccount = useSelector(state => state.account)

	const date = new Date()
	const [poses, setPoses] = useState([])
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [modalTableControl, setModalTableControl] = useState(false)
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [ItemsPerPage, setItemsPerPage] = useState(20)
	const [URL, setURL] = useState("")
	const [pageCount, setPageCount] = useState(0)

	const [filterData, setFilterData] = useState({
		posId: reduxAccount.posId,
		search: '',
		startDate: formatDateBackend(new Date(date.getFullYear(), date.getMonth(), 1)),
		endDate: formatDateBackend(new Date()),
	})
	const [data, setData] = useState([])
	const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })

	const [showEditModal, setShowEditModal] = useState(false)
	const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm({ mode: "onChange" })

	const totals = useMemo(() => {
		let totalSum = 0
		let totalUsed = 0
		let totalBalance = 0
		for (let i = 0; i < data.length; i++) {
			const amount = Number(data[i].amount) || 0
			const used = Number(data[i].used) || 0
			totalSum += amount
			totalUsed += used
			totalBalance += (amount - used)
		}
		return { totalSum, totalUsed, totalBalance }
	}, [data])

	function checkFilter() {
		if (
			filterData.startDate === null &&
			filterData.endDate === null &&
			filterData.posId === null &&
			filterData.search === ''
		) {
			setFilterDataExist(false)
		}
	}

	async function searchWithFilter() {
		var filterUrl = "/services/web/api/loyalty/client-with-bonuses"
		var urlParams = ""
		checkFilter()

		if (filterData.posId)
			urlParams += returnSign(urlParams) + 'posId=' + filterData.posId
		if (filterData.startDate)
			urlParams += returnSign(urlParams) + 'startDate=' + filterData.startDate
		if (filterData.endDate)
			urlParams += returnSign(urlParams) + 'endDate=' + filterData.endDate
		if (filterData.search)
			urlParams += returnSign(urlParams) + 'search=' + filterData.search

		if ("/services/web/api/loyalty/client-with-bonuses" + urlParams !== filterUrl)
			setFilterDataExist(true)

		filterUrl = filterUrl + urlParams
		setURL(filterUrl)

		const response = await PGET("/services/web/api/loyalty/client-with-bonuses", { ...filterData, size: ItemsPerPage }, { loader: true })
		if (response) {
			setData(response.data)
			setPageCount(Math.ceil((response?.headers?.['x-total-count'] || 0) / ItemsPerPage))
		}
		setDropdownIsOpen(false)
	}

	function resetFilter() {
		setFilterData({
			posId: null,
			search: '',
			startDate: null,
			endDate: null,
		})
		setFilterDataExist(false)
		getData()
	}

	async function getData() {
		const response = await PGET("/services/web/api/loyalty/client-with-bonuses", { size: ItemsPerPage }, { loader: true })
		if (response) {
			setData(response.data)
			setPageCount(Math.ceil((response?.headers?.['x-total-count'] || 0) / ItemsPerPage))
		}
	}

	function handleSort(key) {
		setSortConfig(prev =>
			prev.key === key
				? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
				: { key, direction: 'asc' }
		)
	}

	function getSortIcon(key) {
		if (sortConfig.key !== key) return <span className="ms-1 text-muted">↕</span>
		return <span className="ms-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
	}

	function getFilteredData() {
		const search = filterData.search?.toLowerCase()
		let result = data
		if (search) {
			result = result.filter(item => {
				const fullName = `${item.firstName || ''} ${item.lastName || ''}`.toLowerCase()
				const phone = (item.userLogin || '').toLowerCase()
				const card = (item.cardNumber || '').toLowerCase()
				return fullName.includes(search) || phone.includes(search) || card.includes(search)
			})
		}
		if (sortConfig.key) {
			result = [...result].sort((a, b) => {
				let aVal, bVal
				if (sortConfig.key === 'name') {
					aVal = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase()
					bVal = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase()
				} else if (sortConfig.key === 'balance') {
					aVal = Number(a.amount) - Number(a.used)
					bVal = Number(b.amount) - Number(b.used)
				} else {
					aVal = a[sortConfig.key]
					bVal = b[sortConfig.key]
				}
				if (typeof aVal === 'number' && typeof bVal === 'number') {
					return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
				}
				return sortConfig.direction === 'asc'
					? String(aVal || '').localeCompare(String(bVal || ''))
					: String(bVal || '').localeCompare(String(aVal || ''))
			})
		}
		return result
	}

	function exportToExcel() {
		const borderStyle = {
			top: { style: "thin", color: { rgb: "000000" } },
			bottom: { style: "thin", color: { rgb: "000000" } },
			left: { style: "thin", color: { rgb: "000000" } },
			right: { style: "thin", color: { rgb: "000000" } }
		}
		const headerStyle = {
			font: { bold: true },
			border: borderStyle,
			alignment: { horizontal: "center", vertical: "center" }
		}
		const cellStyle = {
			font: { color: { rgb: "000000" } },
			border: borderStyle
		}
		const columnWidths = [
			{ wch: 4 }, { wch: 20 }, { wch: 15 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 16 }
		]
		const rows = [
			[
				{ v: "№", s: headerStyle },
				{ v: t('FIO'), s: headerStyle },
				{ v: t('PHONE'), s: headerStyle },
				{ v: t('CARD_NUMBER'), s: headerStyle },
				{ v: t('TOTAL_SUM'), s: headerStyle },
				{ v: t('USED'), s: headerStyle },
				{ v: t('CURRENT_BALANCE'), s: headerStyle },
			]
		]
		const filtered = getFilteredData()
		for (let i = 0; i < filtered.length; i++) {
			const item = filtered[i]
			rows.push([
				{ v: i + 1, s: cellStyle },
				{ v: `${item.firstName || ''} ${item.lastName || ''}`.trim(), s: cellStyle },
				{ v: item.userLogin || '', s: cellStyle },
				{ v: item.cardNumber || '', s: cellStyle },
				{ v: Number(item.amount) || 0, s: cellStyle },
				{ v: Number(item.used) || 0, s: cellStyle },
				{ v: (Number(item.amount) || 0) - (Number(item.used) || 0), s: cellStyle },
			])
		}
		const ws = XLSX.utils.aoa_to_sheet(rows)
		ws['!cols'] = columnWidths
		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, "SheetJS")
		XLSX.writeFile(wb, t('BONUSES_REPORT') + ".xlsx")
	}

	async function getPoses() {
		const response = await GET('/services/web/api/pos-helper')
		if (response) {
			response.unshift({ 'id': null, 'name': t('ALL') })
			setPoses(response)
		}
	}

	function openEditModal(item) {
		reset()
		setValue('id', item.id)
		setValue('firstName', item.firstName || '')
		setValue('lastName', item.lastName || '')
		setValue('userLogin', item.userLogin || '')
		setValue('cardNumber', item.cardNumber || '')
		setShowEditModal(true)
	}

	function closeEditModal() {
		setShowEditModal(false)
		reset()
	}

	async function updateClient(formValues) {
		const userLoginDigits = regexNumeric(formValues.userLogin || '')
		const payload = {
			id: formValues.id,
			firstName: (formValues.firstName || '').trim(),
			lastName: (formValues.lastName || '').trim(),
			userLogin: userLoginDigits,
			cardNumber: (formValues.cardNumber || '').trim(),
		}
		const response = await PATCH('/services/web/api/loyalty/loyal-cumstomers', payload, { loader: true })
		if (response !== false) {
			setShowEditModal(false)
			reset()
			if (URL) {
				const refreshed = await PGET("/services/web/api/loyalty/client-with-bonuses", { ...filterData, size: ItemsPerPage }, { loader: true })
				if (refreshed) {
					setData(refreshed.data)
					setPageCount(Math.ceil((refreshed?.headers?.['x-total-count'] || 0) / ItemsPerPage))
				}
			} else {
				getData()
			}
		}
	}

	async function paginate(pageData) {
		const response = await PGET(URL || "/services/web/api/loyalty/client-with-bonuses", { ...filterData, page: pageData.selected, size: ItemsPerPage }, { loader: true })
		if (!response) return
		setFilterData({ ...filterData, page: pageData.selected })
		setPageCount(Math.ceil((response?.headers?.['x-total-count'] || 0) / ItemsPerPage))
		setData(response.data || [])
	}

	useEffect(() => {
		getPoses()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		getData()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between mb-2">
				<h4 className="vertical-center">
					{t('BONUSES_REPORT')}
				</h4>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex flex-wrap">
							<div>
								<FilterSearch
									filterData={filterData}
									setFilterData={setFilterData}>
								</FilterSearch>
							</div>
							<div>
								<Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100 me-2">
										{t('FILTER')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('POS')}</label>
												<Select
													options={poses}
													value={poses.find(option => option.id === filterData.posId) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'posId': option.id })}
													placeholder=""
													noOptionsMessage={() => t('LIST_IS_EMPTY')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('PERIOD')}</label>
												<div className="calendar-input">
													<DatePicker
														className="form-control"
														dateFormat="dd.MM.yyyy"
														selected={filterData.startDate ? new Date(filterData.startDate) : ''}
														onChange={(date) => setFilterData({ ...filterData, 'startDate': formatDateBackend(date) })} />
													<i className="uil uil-calendar-alt"></i>
												</div>
											</div>
											<div className="form-group col-md-6">
												<label className="invisible">.</label>
												<div className="calendar-input">
													<DatePicker
														className="form-control"
														dateFormat="dd.MM.yyyy"
														selected={filterData.endDate ? new Date(filterData.endDate) : ''}
														onChange={(date) => setFilterData({ ...filterData, 'endDate': formatDateBackend(date) })} />
													<i className="uil uil-calendar-alt"></i>
												</div>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end">
													<button className="btn btn-secondary me-2" onClick={() => setDropdownIsOpen(false)}>{t('CANCEL')}</button>
													<button className="btn btn-primary" onClick={() => searchWithFilter()}>{t('APPLY')}</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
							<div>
								<button className="btn btn-outline-primary btn-wide" onClick={exportToExcel}>EXCEL</button>
							</div>
						</div>

						<div className="d-flex flex-wrap gap-3">
							<h6 className="mb-0"><b>{t('TOTAL_SUM')}:</b> {formatMoney(totals.totalSum)}</h6>
							<h6 className="mb-0"><b>{t('USED')}:</b> {formatMoney(totals.totalUsed)}</h6>
							<h6 className="mb-0"><b>{t('CURRENT_BALANCE')}:</b> {formatMoney(totals.totalBalance)}</h6>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('FILTER')}</strong>
								<br />
								{(filterData.startDate || filterData.endDate) &&
									<span className="me-2">
										<b>{t('PERIOD')}: </b>
										{filterData.startDate &&
											<span>{formatDate(filterData.startDate)}</span>
										}
										{(filterData.startDate && filterData.endDate) &&
											<span>|</span>
										}
										{filterData.endDate &&
											<span>{formatDate(filterData.endDate)}</span>
										}
									</span>
								}
								{filterData.posId &&
									<span className="me-2">
										<b>{t('POS')}: </b> {findFromArrayById(poses, filterData.posId)}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('SEARCH')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('RESET')}</button>
							</div>
						</div>
					}

					<ItemPerPage
						itemsPerPage={ItemsPerPage}
						setItemsPerPage={(e) => {
							setItemsPerPage(e)
							setFilterData({ ...filterData, page: 0 })
						}}
					/>

					<div className="table-responsive">
						<table className="table table-striped mb-0">
							<thead>
								<tr>
									<th className="text-start cursor" onClick={() => handleSort('name')}>{t('FIO')}{getSortIcon('name')}</th>
									<th className="text-start cursor" onClick={() => handleSort('userLogin')}>{t('PHONE')}{getSortIcon('userLogin')}</th>
									<th className="text-start cursor" onClick={() => handleSort('cardNumber')}>{t('CARD_NUMBER')}{getSortIcon('cardNumber')}</th>
									<th className="text-start cursor" onClick={() => handleSort('amount')}>{t('TOTAL_SUM')}{getSortIcon('amount')}</th>
									<th className="text-start cursor" onClick={() => handleSort('used')}>{t('USED')}{getSortIcon('used')}</th>
									<th className="text-start cursor" onClick={() => handleSort('balance')}>{t('CURRENT_BALANCE')}{getSortIcon('balance')}</th>
									<th className="text-center">{t('ACTION')}</th>
								</tr>
							</thead>
							<tbody>
								{getFilteredData().map((item, index) => {
									return (
										<tr key={index}>
											<td className="text-start text-nowrap">
												{item.firstName}  {item?.lastName}
											</td>
											<td className="text-start text-nowrap">{regexPhoneNumber(item.userLogin)}</td>
											<td className="text-start text-nowrap">{item.cardNumber}</td>
											<td className="text-start text-nowrap">{formatMoney(item.amount)}</td>
											<td className="text-start text-nowrap">{formatMoney(item.used)}</td>
											<td className="text-start text-nowrap text-success">{formatMoney(item.amount - item.used)}</td>
											<td className="d-flex justify-content-center">
												<div className="table-action-button table-action-primary-button" onClick={() => openEditModal(item)}>
													<i className="uil-edit-alt"></i>
												</div>
											</td>
										</tr>
									)
								})}
							</tbody>
						</table>
					</div>
					<Pagination pageCount={pageCount} paginate={paginate} reduxPage={filterData.page}></Pagination>
				</div>
			</div>

			{/* EDIT CLIENT MODAL */}
			<Modal show={showEditModal} animation={false} centered dialogClassName="update-modal-width" backdrop="static" onHide={closeEditModal}>
				<Modal.Header closeButton>
					<Modal.Title>{t('EDIT')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={handleSubmit(updateClient)} autoComplete="off">
						<input type="hidden" {...register('id')} />
						<div className="form-group">
							<label>{t('FIRST_NAME')}<span className="required-mark">*</span></label>
							<input type="text" className="form-control" {...register('firstName', { required: true })} />
							<span className="text-danger text-sm">{errors.firstName?.type === 'required' && t('required')}</span>
						</div>
						<div className="form-group">
							<label>{t('LAST_NAME')}</label>
							<input type="text" className="form-control" {...register('lastName')} />
						</div>
						<div className="form-group">
							<label>{t('PHONE')}<span className="required-mark">*</span></label>
							<input type="text" className="form-control" {...register('userLogin', { required: true })} />
							<span className="text-danger text-sm">{errors.userLogin?.type === 'required' && t('required')}</span>
						</div>
						<div className="form-group">
							<label>{t('CARD_NUMBER')}</label>
							<input type="text" className="form-control" {...register('cardNumber')} />
						</div>
						<div className="d-flex w-100 mt-3">
							<button type="button" className="btn btn-outline-warning w-100 me-2" onClick={closeEditModal}>{t('CANCEL')}</button>
							<button type="submit" className="btn btn-primary w-100">{t('SAVE')}</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>
			{/* EDIT CLIENT MODAL */}

			{/* MODAL TABLE CONTROL */}
			<TableControl
				modalTableControl={modalTableControl}
				setModalTableControl={setModalTableControl}
				keyName={'tableReport'}>
			</TableControl>
			{/* MODAL TABLE CONTROL */}
		</>
	)
}

export default Index
